import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Cog,
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Package,
  AlertTriangle,
  Check,
  Loader2,
  History,
} from "lucide-react";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableTableHead } from "../components/SortableTableHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Parts = () => {
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const { sortKey, sortDir, toggleSort, sortData } = useSortableTable();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPart, setDeletingPart] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    sku: "",
    manufacturer_part_number: "",
    category_id: "",
    supplier_id: "",
    supplier_sku: "",
    lead_time_days: "",
    min_order_qty: "",
    unit_cost: "",
    track_inventory: true,
    initial_stock: "0",
    min_stock_level: "",
  });

  const fetchData = async () => {
    try {
      const [partsRes, categoriesRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/parts`, { withCredentials: true }),
        axios.get(`${API}/part-categories`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
      ]);
      setParts(partsRes.data || []);
      setCategories(categoriesRes.data || []);
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load parts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredParts = parts.filter(part => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!part.name.toLowerCase().includes(query) && 
          !part.sku.toLowerCase().includes(query) &&
          !(part.supplier_name || "").toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Category filter
    if (categoryFilter !== "all" && part.category_id !== categoryFilter) {
      return false;
    }
    
    // Stock filter
    if (stockFilter === "low") {
      const minLevel = part.min_stock_level || 0;
      const current = part.current_stock || 0;
      if (current >= minLevel) return false;
    } else if (stockFilter === "ok") {
      const minLevel = part.min_stock_level || 0;
      const current = part.current_stock || 0;
      if (current < minLevel) return false;
    }
    
    return true;
  });

  const sortedParts = sortData(filteredParts, {
    supplier: (p) => p.supplier_name,
    category: (p) => p.category_name,
    sku: (p) => p.sku,
    name: (p) => p.name,
    cost: (p) => p.unit_cost ?? 0,
    stock: (p) => (p.track_inventory ? p.current_stock ?? 0 : null),
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      sku: "",
      manufacturer_part_number: "",
      category_id: "",
      supplier_id: "",
      supplier_sku: "",
      lead_time_days: "",
      min_order_qty: "",
      unit_cost: "",
      track_inventory: true,
      initial_stock: "0",
      min_stock_level: "",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingPart(null);
    setDialogOpen(true);
  };

  const openEditDialog = (part) => {
    setEditingPart(part);
    setFormData({
      name: part.name || "",
      description: part.description || "",
      sku: part.sku || "",
      manufacturer_part_number: part.manufacturer_part_number || "",
      category_id: part.category_id || "",
      supplier_id: part.supplier_id || "",
      supplier_sku: part.supplier_sku || "",
      lead_time_days: part.lead_time_days?.toString() || "",
      min_order_qty: part.min_order_qty?.toString() || "",
      unit_cost: part.unit_cost?.toString() || "",
      track_inventory: part.track_inventory !== false,
      initial_stock: part.current_stock?.toString() || "0",
      min_stock_level: part.min_stock_level?.toString() || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.sku) {
      toast.error("Name and SKU are required");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        sku: formData.sku,
        manufacturer_part_number: formData.manufacturer_part_number || null,
        category_id: formData.category_id || null,
        supplier_id: formData.supplier_id || null,
        supplier_sku: formData.supplier_sku || null,
        lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null,
        min_order_qty: formData.min_order_qty ? parseInt(formData.min_order_qty) : null,
        unit_cost: parseFloat(formData.unit_cost) || 0,
        track_inventory: formData.track_inventory,
        initial_stock: parseFloat(formData.initial_stock) || 0,
        min_stock_level: formData.min_stock_level ? parseFloat(formData.min_stock_level) : null,
      };
      
      if (editingPart) {
        await axios.put(`${API}/parts/${editingPart.part_id}`, payload, { withCredentials: true });
        toast.success("Part updated");
      } else {
        await axios.post(`${API}/parts`, payload, { withCredentials: true });
        toast.success("Part created");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save part");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPart) return;
    
    try {
      await axios.delete(`${API}/parts/${deletingPart.part_id}`, { withCredentials: true });
      toast.success("Part deleted");
      setDeleteDialogOpen(false);
      setDeletingPart(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete part");
    }
  };

  const getStockStatus = (part) => {
    if (!part.track_inventory) {
      return { label: "—", color: "text-slate-400", icon: null };
    }
    const current = part.current_stock || 0;
    const minLevel = part.min_stock_level || 0;
    
    if (current < minLevel) {
      return { label: current.toString(), color: "text-amber-600", icon: AlertTriangle };
    }
    return { label: current.toString(), color: "text-emerald-600", icon: Check };
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-32"></div>
          <div className="h-96 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="parts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Parts</h1>
          <p className="text-slate-500 mt-1">Manage parts and components for manufacturing</p>
        </div>
        <Button onClick={openCreateDialog} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-2" />
          Add Part
        </Button>
      </div>

      {/* Filters */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search parts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40 rounded-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className="w-40 rounded-sm">
                <SelectValue placeholder="Stock" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stock</SelectItem>
                <SelectItem value="low">Low Stock</SelectItem>
                <SelectItem value="ok">In Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Parts Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {sortedParts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead columnKey="supplier" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Supplier</SortableTableHead>
                  <SortableTableHead columnKey="category" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Category</SortableTableHead>
                  <SortableTableHead columnKey="sku" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>SKU</SortableTableHead>
                  <SortableTableHead columnKey="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Name</SortableTableHead>
                  <SortableTableHead columnKey="cost" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} align="right" className="text-right">Cost</SortableTableHead>
                  <SortableTableHead columnKey="stock" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} align="center" className="text-center">Stock</SortableTableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedParts.map((part) => {
                  const stockStatus = getStockStatus(part);
                  const StockIcon = stockStatus.icon;
                  return (
                    <TableRow 
                      key={part.part_id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openEditDialog(part)}
                      data-testid={`part-row-${part.part_id}`}
                    >
                      <TableCell>
                        {part.supplier_name || <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell>
                        {part.category_name ? (
                          <Badge variant="outline" className="rounded-sm">{part.category_name}</Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">{part.sku}</code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                            <Cog className="h-4 w-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium">{part.name}</p>
                            {part.manufacturer_part_number && (
                              <p className="text-xs text-slate-400">MFR: {part.manufacturer_part_number}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R{part.unit_cost?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className={`flex items-center justify-center gap-1 ${stockStatus.color}`}>
                          {StockIcon && <StockIcon className="h-3.5 w-3.5" />}
                          <span>{stockStatus.label}</span>
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(part)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            {part.cost_history?.length > 1 && (
                              <DropdownMenuItem>
                                <History className="mr-2 h-4 w-4" />
                                Cost History
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => { setDeletingPart(part); setDeleteDialogOpen(true); }}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-lg font-medium">No parts found</p>
              <p className="text-slate-400 text-sm mt-1">
                {searchQuery || categoryFilter !== "all" || stockFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Add your first part to get started"}
              </p>
              {!searchQuery && categoryFilter === "all" && stockFilter === "all" && (
                <Button onClick={openCreateDialog} variant="outline" className="mt-4 rounded-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Part
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stock Legend */}
      <div className="flex items-center gap-6 mt-4 text-sm text-slate-500">
        <div className="flex items-center gap-1.5">
          <Check className="h-4 w-4 text-emerald-600" />
          <span>Above minimum</span>
        </div>
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span>Below minimum</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400">—</span>
          <span>Not tracked</span>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPart ? "Edit Part" : "Add Part"}</DialogTitle>
            <DialogDescription>
              {editingPart ? "Update part details" : "Add a new part to your catalog"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="CO2 Regulator - Dual Gauge"
                  className="rounded-sm mt-1"
                />
              </div>
              
              <div>
                <Label>SKU *</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  placeholder="REG-CO2-DG-001"
                  className="rounded-sm mt-1"
                />
              </div>
              
              <div>
                <Label>Manufacturer Part #</Label>
                <Input
                  value={formData.manufacturer_part_number}
                  onChange={(e) => setFormData({ ...formData, manufacturer_part_number: e.target.value })}
                  placeholder="MFR-REG-2G"
                  className="rounded-sm mt-1"
                />
              </div>
              
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                >
                  <SelectTrigger className="rounded-sm mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Unit Cost (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                  placeholder="450.00"
                  className="rounded-sm mt-1"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Part description..."
                  className="rounded-sm mt-1"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Supplier</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Supplier</Label>
                  <Select 
                    value={formData.supplier_id} 
                    onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
                  >
                    <SelectTrigger className="rounded-sm mt-1">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(sup => (
                        <SelectItem key={sup.supplier_id} value={sup.supplier_id}>{sup.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Supplier SKU</Label>
                  <Input
                    value={formData.supplier_sku}
                    onChange={(e) => setFormData({ ...formData, supplier_sku: e.target.value })}
                    placeholder="ABC-REG-001"
                    className="rounded-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label>Lead Time (days)</Label>
                  <Input
                    type="number"
                    value={formData.lead_time_days}
                    onChange={(e) => setFormData({ ...formData, lead_time_days: e.target.value })}
                    placeholder="7"
                    className="rounded-sm mt-1"
                  />
                </div>
                
                <div>
                  <Label>Min Order Qty</Label>
                  <Input
                    type="number"
                    value={formData.min_order_qty}
                    onChange={(e) => setFormData({ ...formData, min_order_qty: e.target.value })}
                    placeholder="1"
                    className="rounded-sm mt-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Checkbox
                  id="track_inventory"
                  checked={formData.track_inventory}
                  onCheckedChange={(checked) => setFormData({ ...formData, track_inventory: checked })}
                />
                <Label htmlFor="track_inventory" className="cursor-pointer">Track inventory for this part</Label>
              </div>
              
              {formData.track_inventory && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{editingPart ? "Current Stock" : "Initial Stock"}</Label>
                    <Input
                      type="number"
                      value={formData.initial_stock}
                      onChange={(e) => setFormData({ ...formData, initial_stock: e.target.value })}
                      placeholder="10"
                      className="rounded-sm mt-1"
                      disabled={!!editingPart}
                    />
                    {editingPart && (
                      <p className="text-xs text-slate-400 mt-1">Update stock via Inventory</p>
                    )}
                  </div>
                  
                  <div>
                    <Label>Min Stock Level</Label>
                    <Input
                      type="number"
                      value={formData.min_stock_level}
                      onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                      placeholder="5"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              )}
              
              {!editingPart && formData.track_inventory && (
                <p className="text-sm text-slate-500 mt-3 flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  An inventory item will be created automatically
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="rounded-sm">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPart ? "Save Changes" : "Add Part"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Part</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingPart?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Parts;
